import { Direction, Status } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { SMS, Thread } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { KnownThreadStatus } from '../../../types';

export const upsertSmsOntoThread = (thread: Thread, sms: SMS | SMS[], messageLimit = 1): Thread => {
  if (Array.isArray(sms)) {
    return sms.reduce<Thread>((acc, curr) => upsertSmsOntoThread(acc, curr, messageLimit), thread);
  }

  const originalLatestSms = thread.messages[0];
  if (!originalLatestSms) {
    return {
      ...thread,
      messages: [sms],
    };
  }

  const hasMatchingSms = thread.messages.some((existingSms) => existingSms.id === sms.id);
  let hasUpsertedSms = false;
  const newlyOrderedMessages = thread.messages.reduce<SMS[]>((acc, curr) => {
    if (hasMatchingSms) {
      if (curr.id === sms.id) {
        acc.push(sms);
        hasUpsertedSms = true;
      }
      return acc;
    }

    if (new Date(sms.createdAt).getTime() > new Date(curr.createdAt).getTime()) {
      acc.push(sms);
      hasUpsertedSms = true;
    }
    acc.push(curr);
    return acc;
  }, []);
  if (!hasUpsertedSms) newlyOrderedMessages.push(sms);

  const limitedNewMessages =
    messageLimit >= newlyOrderedMessages.length ? newlyOrderedMessages : newlyOrderedMessages.slice(0, messageLimit);
  const newLatestSms = limitedNewMessages[0];

  if (!newLatestSms) {
    return {
      ...thread,
      messages: limitedNewMessages,
    };
  }

  const isDifferentSms = originalLatestSms.id !== newLatestSms.id;
  const originalIsOlder = originalLatestSms.createdAt < newLatestSms.createdAt;
  let newStatus = thread.status;
  if (newLatestSms.status === Status.STATUS_ERROR) {
    newStatus = KnownThreadStatus.ERROR;
  } else if (newLatestSms.direction === Direction.DIRECTION_INBOUND && newLatestSms.status !== Status.STATUS_READ) {
    newStatus = KnownThreadStatus.NEW;
  } else if (!newLatestSms.autogeneratedBy) {
    newStatus = KnownThreadStatus.READ;
  }

  if (!isDifferentSms || originalIsOlder) {
    return {
      ...thread,
      messages: limitedNewMessages,
      status: newStatus,
      actionable:
        isDifferentSms && newLatestSms.direction === Direction.DIRECTION_OUTBOUND && !newLatestSms.autogeneratedBy
          ? false
          : thread.actionable,
    };
  }

  return { ...thread, messages: limitedNewMessages };
};
